import { FC } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Text,
} from '@fluentui/react-components';
import { XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SyButton from '../Common/SyButton';
import './SurveyDialog.css';

export interface SurveyCloseDialogProps {
    open: boolean;
    surveyName?: string;
    onClose: () => void;
    onConfirm: () => void;
    isClosing?: boolean;
}

const SurveyCloseDialog: FC<SurveyCloseDialogProps> = ({
    open,
    surveyName,
    onClose,
    onConfirm,
    isClosing = false,
}) => {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
            <DialogSurface className="survey-dialog-surface">
                <DialogBody className="survey-dialog-body">
                    <DialogTitle className="survey-dialog-title">
                        <XCircle size={24} className="survey-dialog-icon-gray" />
                        <Text className="survey-dialog-title-text">
                            {t('adminSettings.surveys.closeDialog.title', 'Close survey')}
                        </Text>
                    </DialogTitle>
                    <DialogContent className="survey-dialog-content">
                        <Text className="survey-dialog-message-text">
                            {t(
                                'adminSettings.surveys.closeDialog.message',
                                'Are you sure you want to close the survey'
                            )}
                            {surveyName && (
                                <>
                                    {' '}
                                    <span className="survey-dialog-survey-name">" {surveyName}"</span>
                                </>
                            )}
                            ?
                        </Text>
                        <div>
                            <Text className="survey-dialog-warning-text survey-dialog-warning-amber">
                                {t(
                                    'adminSettings.surveys.closeDialog.warning',
                                    'You will not be able to reactivate the survey.'
                                ).split('\n').map((line, i, arr) => (
                                    <span key={i}>
                                        {line}
                                        {i < arr.length - 1 && <br />}
                                    </span>
                                ))}
                            </Text>
                        </div>
                    </DialogContent>
                    <DialogActions className="survey-dialog-actions">
                        <SyButton
                            appearance="secondary"
                            onClick={onClose}
                            disabled={isClosing}
                        >
                            {t('cancel', 'Cancel')}
                        </SyButton>
                        <SyButton
                            appearance="primary"
                            onClick={onConfirm}
                            disabled={isClosing}
                            icon={<XCircle size={16} />}
                        >
                            {isClosing
                                ? t('adminSettings.surveys.closing', 'Closing...')
                                : t('adminSettings.surveys.close', 'Close survey')}
                        </SyButton>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default SurveyCloseDialog;
